import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/api';

/** Mirrors the backend `NotificationPayload` (notification-events.ts). */
export interface NotificationPayload {
  id: number;
  organizationId: number;
  userId: number | null;
  scope: 'ORGANIZATION' | 'USER';
  type: string;
  title: string;
  body: string | null;
  data: Record<string, unknown>;
  scopeType: string | null;
  scopeId: number | null;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationListResponse {
  items: NotificationPayload[];
  nextCursor: number | null;
}

export type NotificationPreferences = Record<string, boolean>;

export const notificationKeys = {
  all: ['notifications'] as const,
  list: () => [...notificationKeys.all, 'list'] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
  preferences: () => [...notificationKeys.all, 'preferences'] as const,
};

export function useNotifications(enabled = true) {
  return useQuery({
    queryKey: notificationKeys.list(),
    queryFn: () => api.get<NotificationListResponse>('/notifications'),
    enabled,
  });
}

export function useUnreadCount(enabled = true) {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: () => api.get<number>('/notifications/unread-count'),
    enabled,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.patch<{ success: boolean }>(`/notifications/${id}/read`),
    onSuccess: (_data, id) => {
      // Optimistically flip the row and decrement the badge; the socket/refetch
      // remain the source of truth.
      queryClient.setQueryData<NotificationListResponse>(notificationKeys.list(), (prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((n) =>
                n.id === id && !n.readAt ? { ...n, readAt: new Date().toISOString() } : n,
              ),
            }
          : prev,
      );
      queryClient.setQueryData<number>(notificationKeys.unreadCount(), (count) =>
        typeof count === 'number' ? Math.max(0, count - 1) : count,
      );
    },
  });
}

export function useNotificationPreferences(enabled = true) {
  return useQuery({
    queryKey: notificationKeys.preferences(),
    queryFn: () => api.get<NotificationPreferences>('/notifications/preferences'),
    enabled,
  });
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (preferences: NotificationPreferences) =>
      api.patch<NotificationPreferences>('/notifications/preferences', { preferences }),
    onSuccess: (data) => {
      queryClient.setQueryData(notificationKeys.preferences(), data);
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.patch<{ count: number }>('/notifications/read-all'),
    onSuccess: () => {
      queryClient.setQueryData<number>(notificationKeys.unreadCount(), 0);
      void queryClient.invalidateQueries({ queryKey: notificationKeys.list() });
    },
  });
}
